import React, { createContext, useContext, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

export interface AccordionProps {
  dataCy?: string;
  label: string;
  onToggle?: (a: boolean) => void;
  children: React.ReactNode;
  className: string;
  animateStylesStyles?: {
    height: string;
    transitionDelay: string;
    transition: string;
  };
}

const AccordionContext = createContext<any>({});

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(
      'Accordion compound components cannot be rendered outside the Accordion component',
    );
  }
  return context;
};

const Toggle = (props: {
  children?: any;
  className?: string;
  borderClassName?: string;
}) => {
  const { onToggle, isOpen, setIsOpen } = useAccordionContext();
  return (
    <div className={`flex flex-col rounded-lg ${props.className}`}>
      <div
        className={classNames(`flex justify-between items-center px-4 py-2`, {
          'border-neutral-gray-300': isOpen && props.borderClassName,
          'border-b': !props.borderClassName,
          [props.borderClassName as string]: props.borderClassName,
        })}
        onClick={() => {
          if (onToggle) {
            onToggle(!isOpen);
          }
          setIsOpen(!isOpen);
        }}
      >
        {props.children}
        {isOpen ? (
          <ChevronUpIcon className={'h-8 w-8 stroke-1'} />
        ) : (
          <ChevronDownIcon className={'h-8 w-8 stroke-1'} />
        )}
      </div>
    </div>
  );
};

const Panel = (props: { children: any; openClassName?: string }) => {
  const { isOpen, animateStyles } = useAccordionContext();
  if (!isOpen && !animateStyles) {
    return null;
  }
  return (
    <div
      className={isOpen && props.openClassName}
      style={
        animateStyles
          ? {
              height: isOpen ? animateStyles.height || '400px' : 0,
              transition: animateStyles.transition || 'height 0.4s ease-in-out',
              ...animateStyles,
            }
          : {}
      }
    >
      {props.children}
    </div>
  );
};

export const Accordion = ({
  label,
  onToggle,
  children,
  className,
  open = false,
  animateStyles,
  borderStyles = 'border border-neutral-gray-300',
  roundingStyles = 'rounded-lg',
}: {
  label?: string;
  onToggle?: () => void;
  children?: React.ReactNode[] | React.ReactNode;
  className?: string;
  open?: boolean;
  animateStyles?: boolean;
  borderStyles?: string;
  roundingStyles?: string;
}) => {
  const [isOpen, setIsOpen] = useState(open);

  const value = useMemo(
    () => ({ label, onToggle, isOpen, setIsOpen, animateStyles }),
    [label, onToggle, isOpen, setIsOpen],
  );

  return (
    <AccordionContext.Provider value={value}>
      <div
        className={classNames(
          'w-full h-full bg-white',
          className,
          borderStyles,
          roundingStyles,
        )}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

Accordion.Toggle = Toggle;
Accordion.Panel = Panel;
Accordion.filePath = 'libs/shared/ui-kit/src/lib/accordion/accordion.tsx';
Accordion.displayName = 'Accordion';
